import {gql} from 'graphql-request';
import {UserProfile} from '@/interface';
import {useMutation} from '@tanstack/react-query';
import {useGqlRequest} from './request';
import {USER_PROFILE_RESULT, useRefetchMe} from './session';

export type WithdrawalReason =
  | 'ServiceDissatisfaction'
  | 'LowUsage'
  | 'DataIssue'
  | 'PrivacyConcern'
  | 'SwitchedService'
  | 'Expensive'
  | 'Other'
  | 'AdminDeletion';

export const useUpdateMarketingConsent = () => {
  const request = useGqlRequest({
    query: UPDATE_MARKETING_CONSENT,
  });
  const refetchMe = useRefetchMe();

  const mutationFn = (
    agree: boolean
  ): Promise<{
    result?: {id: string};
  }> => {
    return request({agree});
  };

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      if (data?.result?.id) {
        refetchMe();
      }
    },
  });
};

export const useChangePassword = () => {
  const request = useGqlRequest({
    query: CHANGE_PASSWORD,
  });

  const mutationFn = (record: {
    newPassword: string;
    oldPassword: string;
  }): Promise<{
    result?: boolean;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

export const useUpdateProfile = () => {
  const request = useGqlRequest({
    query: UPDATE_PROFILE,
  });
  const refetchMe = useRefetchMe();

  const mutationFn = (
    record: UserProfile
  ): Promise<{
    result?: {profile: UserProfile};
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      if (data?.result?.profile) {
        refetchMe();
      }
    },
  });
};

export const useWithdrawUser = () => {
  const request = useGqlRequest({
    query: WITHDRAW_USER,
  });

  const mutationFn = (record: {
    reason: WithdrawalReason;
    reasonEtc?: string;
  }): Promise<{
    result?: {
      success: boolean;
      message?: string;
      needCancelSubscription?: boolean;
    };
  }> => {
    return request(record);
  };

  return useMutation({mutationFn});
};

const WITHDRAW_USER = gql`
  mutation WithdrawUser($reason: WithdrawalReason!, $reasonEtc: String) {
    result: withdrawUser(reason: $reason, reasonEtc: $reasonEtc) {
      success
      message
      needCancelSubscription
    }
  }
`;

const UPDATE_MARKETING_CONSENT = gql`
  mutation updateMarketingConsent($agree: Boolean!) {
    result: updateMarketingConsent(agree: $agree) {
      id
    }
  }
`;

const CHANGE_PASSWORD = gql`
  mutation changePassword($newPassword: String!, $oldPassword: String!) {
    result: changePassword(newPassword: $newPassword, oldPassword: $oldPassword)
  }
`;

const UPDATE_PROFILE = gql`
  mutation updateProfile(
    $ageGroup: UserAgeGroup!
    $category: UserCategory!
    $otherCategory: String
    $gender: UserGender!
    $purpose: [UserPurpose!]!
    $other: String
  ) {
    result: updateProfile(
      ageGroup: $ageGroup
      category: $category
      otherCategory: $otherCategory
      gender: $gender
      purpose: $purpose
      other: $other
    ) {
      profile {
        ${USER_PROFILE_RESULT}
      }
    }
  }
`;
